/**
 * The vocabulary of a deterministic intraday schedule: its kinds, its defaults
 * and the ranges the API enforces.
 *
 * **One copy, read by everything that states a bound.** `parseIntradayConfig`
 * enforces these, the route's `invalid_configuration` message is built from
 * them, and the terminal's help prints them. Before `M7/I3` the numbers lived
 * only inside the parser, so a help page that said `lookback 2–390` would have
 * been a second copy, true only until somebody changed the first
 * (`DESIGN-cli-command-spec`).
 */

export const INTRADAY_KINDS = ["dca", "band", "shares"] as const;
export type IntradayKind = (typeof INTRADAY_KINDS)[number];

export const isIntradayKind = (kind: string): kind is IntradayKind =>
  (INTRADAY_KINDS as readonly string[]).includes(kind);

/**
 * The kinds that take a take profit and stop loss (`ADR-0044`, `ADR-0046`):
 * the ones that only buy on their own. A band schedule sells on its own rule.
 */
export const hasExits = (kind: IntradayKind): boolean => kind === "dca" || kind === "shares";

/**
 * What a field is when the request leaves it out.
 *
 * Amounts and share counts are decimal strings, never numbers: they are parsed
 * by `money()` and `qty()`, and a JSON number here would be a float standing
 * for money.
 */
export const INTRADAY_DEFAULTS = {
  notional: "100",
  /** Shares per buy, for a `shares` schedule (`M8/I10`). */
  qty: "0.1",
  tranche: "100",
  core: "0",
  max_position: "100",
  band_bps: 10,
  lookback: 30,
  interval_minutes: 1,
} as const;

/** Inclusive bounds on the integer fields. */
export const INTRADAY_RANGES = {
  band_bps: { min: 1, max: 10_000 },
  lookback: { min: 2, max: 390 },
  interval_minutes: { min: 1, max: 390 },
  /** How far under the lookback mean a `dca` or `shares` buy waits for (M8/I11). */
  below_bps: { min: 1, max: 10_000 },
  /** How long a `dca` or `shares` schedule waits after an exit before buying (M8/I11). */
  cooldown_minutes: { min: 1, max: 1_440 },
} as const;

/**
 * What a DCA schedule does when a take profit or stop loss is hit (M8/E4,
 * `ADR-0044`): sell the whole position and stop, sell it and keep buying, or
 * stop and keep the shares.
 */
export const INTRADAY_EXIT_ACTIONS = ["sell_stop", "sell_continue", "stop"] as const;
export type IntradayExitAction = (typeof INTRADAY_EXIT_ACTIONS)[number];
export const INTRADAY_EXIT_DEFAULT: IntradayExitAction = "sell_stop";
export const isIntradayExitAction = (value: unknown): value is IntradayExitAction =>
  (INTRADAY_EXIT_ACTIONS as readonly unknown[]).includes(value);

/** The exit fields, each optional and off when absent. Percentages are of the cost basis. */
export const INTRADAY_EXIT_FIELDS = [
  "take_profit_pct",
  "take_profit_usd",
  "stop_loss_pct",
  "stop_loss_usd",
] as const;
export type IntradayExitField = (typeof INTRADAY_EXIT_FIELDS)[number];

/** A band schedule ticks every minute, whatever it is asked for. */
export const BAND_INTERVAL_MINUTES = 1;

const span = (field: keyof typeof INTRADAY_RANGES) =>
  `${INTRADAY_RANGES[field].min}–${INTRADAY_RANGES[field].max}`;

/** The sentence a refused configuration carries, from the same numbers the parser checks. */
export const INTRADAY_CONFIGURATION_MESSAGE =
  "Amounts must be positive decimal strings; core/max are shares; " +
  `lookback ${span("lookback")}, band ${span("band_bps")} bps, ` +
  `interval ${span("interval_minutes")} minutes (band always ${BAND_INTERVAL_MINUTES}). ` +
  "shares: qty is a positive share string, at most max. " +
  "DCA and shares only: take_profit_pct/take_profit_usd/stop_loss_pct/stop_loss_usd are positive decimal " +
  `strings (stop_loss_pct below 100); exit_action is ${INTRADAY_EXIT_ACTIONS.join("|")}; ` +
  `below_bps ${span("below_bps")}, cooldown_minutes ${span("cooldown_minutes")}, flat_weekend a boolean.`;
